/**
 * Page-level consent event.
 *
 * Every time the SDK applies a set of choices — an explicit submit, a choice
 * stored on a previous visit, a Global Privacy Control auto-deny, the
 * opt-out defaults of a non-EU visitor, or a reset — it dispatches
 * `csteer:consent` on `window`. Host pages and integrations (the WordPress
 * plugin bridges it to the WP Consent API) can react without relying on the
 * programmatic `init()` callbacks, which the drop-in `<script>` cannot wire.
 *
 *   window.addEventListener("csteer:consent", (e) => {
 *     const { choices, source, mode } = e.detail;
 *   });
 *
 * `choices` is null on `reset` (the banner is about to ask again).
 */

import type { ConsentChoices } from "./types.js";

export type ConsentEventSource = "submit" | "stored" | "gpc" | "geo-optout" | "reset";

export interface ConsentEventDetail {
  choices: ConsentChoices | null;
  source: ConsentEventSource;
  /** Legal basis in force for this visitor: opt-in (EU default) or opt-out. */
  mode: "opt-in" | "opt-out";
}

export const CONSENT_EVENT = "csteer:consent";

export function emitConsentEvent(detail: ConsentEventDetail): void {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
  try {
    window.dispatchEvent(new CustomEvent<ConsentEventDetail>(CONSENT_EVENT, { detail }));
  } catch {
    // A listener that throws must never break the consent flow.
  }
}
